import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, apiPut, apiPost, PortalApiError } from "../api/portal-client";
import { usePortalAuth } from "../hooks/usePortalAuth";
import { usePortalResource } from "../hooks/usePortalResource";
import { DASH } from "../lib/format";

/**
 * Mon profil — coordonnées de facturation et mot de passe.
 *
 * W7-B — l'écran ne se réduit plus à « Chargement... » nu puis à « Profil
 * introuvable » : ces deux retours anticipés faisaient disparaître le titre, le
 * shell et toute possibilité de reprise, et confondaient une coupure réseau, un
 * accès coupé par l'artisan (403) et une panne serveur. Le H1 tient dans tous
 * les états, l'état est nommé, et l'erreur se reprend sans recharger la page.
 *
 * Le parcours de mot de passe FORCÉ est inchangé : mêmes appels, même sortie par
 * la reconnexion (le jeton courant ne donne accès à rien d'autre).
 */
interface Profile {
  id: string;
  name: string;
  email: string | null;
  billingEmail: string | null;
  phone: string | null;
  billingStreet: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  siren: string | null;
  siretClient: string | null;
  vatNumberClient: string | null;
  portalUsername: string | null;
  artisanName: string;
}

export function ProfilePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = usePortalAuth();
  const forceChangePassword = searchParams.get("changePassword") === "1";

  const fiche = usePortalResource<Profile>(() => apiGet<Profile>("/me"));
  // Copie locale : elle porte le résultat du PUT (le hook, lui, ne connaît que
  // ce que le GET a renvoyé) — sans elle, un enregistrement réussi réafficherait
  // les anciennes coordonnées jusqu'au prochain rechargement.
  const [profile, setProfile] = useState<Profile | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<Partial<Profile>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState<string | null>(null);
  const [showPwdSection, setShowPwdSection] = useState(forceChangePassword);

  useEffect(() => {
    if (!fiche.data) return;
    setProfile(fiche.data);
    setForm(fiche.data);
  }, [fiche.data]);

  async function handleSave() {
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        email: form.email ?? undefined,
        billingEmail: form.billingEmail ?? undefined,
        phone: form.phone ?? undefined,
        billingStreet: form.billingStreet ?? undefined,
        billingPostalCode: form.billingPostalCode ?? undefined,
        billingCity: form.billingCity ?? undefined,
      };
      const updated = await apiPut<Profile>("/me", payload);
      setProfile(updated);
      setForm(updated);
      setEditMode(false);
      setSuccess("Vos informations ont été mises à jour.");
    } catch (e: unknown) {
      setError(e instanceof PortalApiError ? e.message : "Erreur de sauvegarde");
    }
  }

  async function handleChangePassword() {
    setPwdError(null);
    setPwdSuccess(null);
    if (newPassword.length < 8) {
      setPwdError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError("Les mots de passe ne correspondent pas.");
      return;
    }
    try {
      await apiPost("/auth/change-password", { currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      localStorage.setItem("portal_mcp", "0");
      // Le JWT courant est restreint au changement de mot de passe : il ne
      // donnera jamais accès au reste du portail. On repasse par la connexion.
      if (forceChangePassword) {
        setPwdSuccess("Mot de passe modifié. Reconnectez-vous avec votre nouveau mot de passe.");
        logout();
        navigate("/", { replace: true });
        return;
      }
      setPwdSuccess("Mot de passe modifié.");
    } catch (e: unknown) {
      setPwdError(e instanceof PortalApiError ? e.message : "Erreur");
    }
  }

  return (
    <main className="max-w-2xl mx-auto px-4 py-6">
      {/* Le retour au tableau de bord appartient au shell, qui le masque de
          lui-même pendant un changement de mot de passe forcé : le reste du
          portail est alors fermé côté API. */}
      <PageHeader title="Mon profil" />
      {forceChangePassword && (
        <p className="text-sm text-ink bg-card border border-border rounded-xl2 p-4 mb-6">
          Pour accéder à votre espace, choisissez d'abord un nouveau mot de passe.
        </p>
      )}

      <div className="mb-6">
        <PortalState
          status={fiche.status}
          label="vos informations"
          error={fiche.error}
          onRetry={fiche.reload}
        />
      </div>

      {/* Le bloc « mot de passe » reste accessible même quand la fiche ne charge
          pas : il ne dépend d'aucune donnée lue, et c'est justement l'écran dont
          un client a besoin quand son accès vient d'être réinitialisé. */}
      {profile && (
        <div className="bg-card border border-border rounded-xl2 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-ink">Coordonnées</h2>
            {!editMode && !forceChangePassword && (
              <Button variant="ghost" size="sm" onClick={() => setEditMode(true)}>Modifier</Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ReadOnlyField label="Raison sociale" value={profile.name} />
            <ReadOnlyField label="SIREN / SIRET" value={profile.siretClient || profile.siren} />
            <ReadOnlyField label="N° TVA" value={profile.vatNumberClient} />
            <ReadOnlyField label="Identifiant portail" value={profile.portalUsername} />

            <EditableField editMode={editMode} label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
            <EditableField editMode={editMode} label="Email facturation" value={form.billingEmail} onChange={(v) => setForm({ ...form, billingEmail: v })} type="email" />
            <EditableField editMode={editMode} label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <EditableField editMode={editMode} label="Adresse" value={form.billingStreet} onChange={(v) => setForm({ ...form, billingStreet: v })} />
            <EditableField editMode={editMode} label="Code postal" value={form.billingPostalCode} onChange={(v) => setForm({ ...form, billingPostalCode: v })} />
            <EditableField editMode={editMode} label="Ville" value={form.billingCity} onChange={(v) => setForm({ ...form, billingCity: v })} />
          </div>

          {editMode && (
            <div className="flex gap-2 mt-5">
              <Button onClick={handleSave}>Enregistrer</Button>
              <Button variant="ghost" onClick={() => { setEditMode(false); setForm(profile); }}>Annuler</Button>
            </div>
          )}
          {success && <p className="text-sm text-success mt-3">{success}</p>}
          {error && (
            <p role="alert" className="text-sm text-danger mt-3">
              {error}
            </p>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-xl2 p-5">
        <button
          className="w-full flex items-center justify-between text-left"
          onClick={() => setShowPwdSection((v) => !v)}
          aria-expanded={showPwdSection}
        >
          <h2 className="font-semibold text-ink">Mot de passe</h2>
          <span className="text-sm text-muted">{showPwdSection ? "−" : "+"}</span>
        </button>

        {showPwdSection && (
          <div className="mt-4 flex flex-col gap-3">
            <Input label="Mot de passe actuel" name="currentPassword" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            <Input label="Nouveau mot de passe (min. 8 caractères)" name="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <Input label="Confirmer le nouveau mot de passe" name="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            <p className="text-xs text-muted">
              Votre mot de passe n'est jamais transmis par email : vous seul le connaissez.
            </p>
            <Button onClick={handleChangePassword} disabled={!currentPassword || !newPassword || !confirmPassword}>
              Changer le mot de passe
            </Button>
            {pwdError && (
              <p role="alert" className="text-sm text-danger">
                {pwdError}
              </p>
            )}
            {pwdSuccess && <p className="text-sm text-success">{pwdSuccess}</p>}
          </div>
        )}
      </div>
    </main>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-xs text-muted font-medium">{label}</p>
      <p className="text-sm text-ink mt-1">{value || DASH}</p>
    </div>
  );
}

function EditableField({
  editMode,
  label,
  value,
  onChange,
  type = "text",
}: {
  editMode: boolean;
  label: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  type?: string;
}) {
  if (editMode) {
    return <Input label={label} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return <ReadOnlyField label={label} value={value} />;
}
